import React, { useEffect, useState } from 'react'
import ReactSpeedometer from 'react-d3-speedometer'
import { format, subMonths, addMonths } from 'date-fns'
import { formatLocalCap } from 'utils/date'

import { translate } from 'locales'
import { Header, Card, Loading, Button, Title } from 'components'
import * as Global from 'global/styles.global'
import { ChevronLeft, ChevronRight } from '@styled-icons/boxicons-regular'
import * as S from './styles'

import { getCsat, fetchDoctorRatingFromMe } from './services'
import ratingScreenAnalytics from 'services/analytics/Rating/ratingScreenAnalytics'

const Rating = () => {
  const [month, setMonth] = useState(new Date())
  const [fixedRating, setFixedRating] = useState(0)
  const [ratings, setRatings] = useState<any[]>([])
  const [comments, setComments] = useState<string[]>([])
  const [loadingCsat, setLoadingCsat] = useState(false)
  const [loadingMe, setLoadingMe] = useState(false)

  const dataInStorage: any = window.localStorage.getItem('@StarBem:user')
  const dataParsed = JSON.parse(dataInStorage)

  const changeDate = (value: Date) => {
    setMonth(value)
  }

  const verifyMonth = format(month, 'MM-yyyy') === format(new Date(), 'MM-yyyy')

  useEffect(() => {
    ratingScreenAnalytics.trackScreenView()
    setLoadingMe(true)
    fetchDoctorRatingFromMe()
      .then((rating) => {
        setFixedRating(rating)
        ratingScreenAnalytics.trackDoctorRatingLoaded(rating)
      })
      .finally(() => setLoadingMe(false))
  }, [])

  useEffect(() => {
    getCsat(
      dataParsed.id,
      month,
      setRatings,
      setComments,
      setLoadingCsat,
      ({ commentsCount, period }) => {
        ratingScreenAnalytics.trackCsatLoaded({
          doctor_id: dataParsed.id,
          period,
          comments_count: commentsCount,
        })
      }
    )
  }, [dataParsed.id, month])

  const loading = loadingMe || loadingCsat

  const colors: any = {
    1: 'rgb(106, 215, 45)',
    2: 'rgb(174, 226, 40)',
    3: 'rgb(236, 219, 35)',
    4: 'rgb(246, 150, 30)',
    5: 'rgb(255, 71, 26)',
  }

  return (
    <Global.Container>
      {loading && <Loading title={translate('rating.loading')} />}
      <Header />
      <S.Content data-testid="rating-screen">
        <h1 className="title-config">
          {translate('rating.evaluatedServices')}
        </h1>
        <S.ContentCard
          styles={{ display: 'flex', flexDirection: 'row', paddingBottom: 0 }}
        >
          <S.ContentCharts>
            <ReactSpeedometer
              paddingVertical={15}
              valueTextFontSize="30px"
              value={fixedRating}
              minValue={0}
              maxValue={5}
              needleColor="steelblue"
              needleTransitionDuration={1500}
            />
          </S.ContentCharts>
          <S.ContentEmotions>
            {ratings?.map(
              (item: { name: string; number: number; count: string }) => (
                <S.CardEmotions key={item.name} color={colors[item.number]}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <S.EmotionNumber color={colors[item.number]}>
                      {item.count}
                    </S.EmotionNumber>
                    <S.EmotionName>{item.name}</S.EmotionName>
                  </div>
                </S.CardEmotions>
              )
            )}
          </S.ContentEmotions>
        </S.ContentCard>
        <Card styles={{ paddingTop: 0 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: 16,
            }}
          >
            <Title text={translate('rating.comments')} />
            <div
              style={{ display: 'flex', alignItems: 'center' }}
              data-testid="rating-month-filter"
            >
              <Button
                color="primary"
                type="button"
                size="small"
                styles={{ marginRight: 15 }}
                onClick={() => {
                  ratingScreenAnalytics.trackMonthFilterChange({
                    direction: 'previous',
                    from_period: format(month, 'yyyy-MM'),
                  })
                  changeDate(subMonths(month, 1))
                }}
              >
                <ChevronLeft size={30} color="#fff" />
              </Button>
              <h4 style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>
                {formatLocalCap(month, 'MMM yyyy')}
              </h4>
              <Button
                color="primary"
                size="small"
                styles={{ marginLeft: 15 }}
                onClick={() => {
                  ratingScreenAnalytics.trackMonthFilterChange({
                    direction: 'next',
                    from_period: format(month, 'yyyy-MM'),
                  })
                  changeDate(addMonths(month, 1))
                }}
                type="button"
                disabled={verifyMonth}
              >
                <ChevronRight size={30} color="#fff" />
              </Button>
            </div>
          </div>
          {comments.length > 0 ? (
            <S.ContentComents>
              {comments.map((comment: string, index: number) => (
                <S.CardComent key={index}>
                  <S.ComentName>{comment}</S.ComentName>
                </S.CardComent>
              ))}
            </S.ContentComents>
          ) : (
            <p>{translate('rating.noComments')}</p>
          )}
        </Card>
      </S.Content>
    </Global.Container>
  )
}

export default Rating
